import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { loadGoogleMaps, onMapsAuthFailure } from '@/lib/googleMaps'
import { lastSeenLabel, locationFreshness } from '@/lib/riderLocationBuffer'
import type { DispatchBoard, DispatchRider, RiderTicket } from '@/types/rider'

// Live map for the Rider Management page (F-DLV-N2): every rider's last position
// (green live, amber stale, grey lost), every open drop pin (amber when no
// rider has it, the rider's colour when one does), and the trail of the rider
// you click. Uses the tenant's Google Maps key from Delivery Zones.

const FALLBACK_CENTER = { lat: 31.5204, lng: 74.3587 }

// Distinct, colour-blind-friendly-ish rider colours, assigned in board order.
const RIDER_COLOURS = ['#2563eb', '#db2777', '#7c3aed', '#0d9488', '#ea580c', '#4f46e5', '#65a30d', '#0891b2']

const FRESHNESS_COLOUR = { live: '#16a34a', stale: '#d97706', lost: '#9ca3af' } as const

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

async function fetchMapsKey(): Promise<string> {
  try {
    const res = await apiClient.getSetting('google_maps_api_key')
    return res.success && typeof res.data === 'string' ? res.data : ''
  } catch {
    return ''
  }
}

export function DispatchMap({ board, now }: { board: DispatchBoard; now: number }) {
  const { data: apiKey, isLoading } = useQuery({
    queryKey: ['settings', 'google_maps_api_key'],
    queryFn: fetchMapsKey,
    staleTime: 5 * 60_000,
  })
  const divRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<google.maps.Marker[]>([])
  const trailRef = useRef<google.maps.Polyline | null>(null)
  const infoRef = useRef<google.maps.InfoWindow | null>(null)
  const fittedRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)

  const colourOf = useMemo(() => {
    const m = new Map<string, string>()
    board.riders.forEach((r, i) => m.set(r.id, RIDER_COLOURS[i % RIDER_COLOURS.length]))
    return m
  }, [board.riders])

  useEffect(() => {
    if (!apiKey) return
    let cancelled = false
    const off = onMapsAuthFailure(() => setError('Google rejected the Maps key — check it under Online → Delivery zones.'))
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !divRef.current) return
        mapRef.current = new maps.Map(divRef.current, {
          center: FALLBACK_CENTER,
          zoom: 12,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          clickableIcons: false,
        })
        infoRef.current = new maps.InfoWindow()
        setReady(true)
      })
      .catch((e: Error) => setError(e.message))
    return () => {
      cancelled = true
      off()
    }
  }, [apiKey])

  // Redraw markers whenever the board refreshes.
  useEffect(() => {
    const map = mapRef.current
    if (!ready || !map) return
    markersRef.current.forEach((m) => m.setMap(null))
    markersRef.current = []
    const bounds = new google.maps.LatLngBounds()
    let any = false

    const drop = (t: RiderTicket) => {
      if (t.lat == null || t.lng == null) return
      const colour = t.rider_id ? colourOf.get(t.rider_id) ?? '#2563eb' : '#f59e0b'
      const marker = new google.maps.Marker({
        map,
        position: { lat: t.lat, lng: t.lng },
        title: `#${t.order_number} · ${t.customer_name ?? ''}`,
        label: { text: t.order_number.slice(-3), color: '#ffffff', fontSize: '10px', fontWeight: '600' },
        icon: {
          path: 'M12 0C7 0 3 4 3 9c0 6.5 9 15 9 15s9-8.5 9-15c0-5-4-9-9-9z',
          fillColor: colour,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 1.5,
          scale: 1.6,
          anchor: new google.maps.Point(12, 24),
          labelOrigin: new google.maps.Point(12, 9),
        },
        zIndex: 1,
      })
      marker.addListener('click', () => {
        infoRef.current?.setContent(
          `<div style="font:13px system-ui;min-width:180px"><b>#${t.order_number}</b><br/>${escapeHtml(
            t.customer_name ?? 'Customer',
          )}<br/><span style="color:#6b7280">${escapeHtml(t.delivery_address ?? '')}</span><br/>${
            t.rider_name ? `Rider: ${escapeHtml(t.rider_name)}` : '<b style="color:#d97706">No rider yet</b>'
          }</div>`,
        )
        infoRef.current?.open({ map, anchor: marker })
      })
      markersRef.current.push(marker)
      bounds.extend({ lat: t.lat, lng: t.lng })
      any = true
    }
    board.open.forEach(drop)

    const riderMarker = (r: DispatchRider) => {
      if (!r.location) return
      const at = new Date(r.location.recorded_at).getTime()
      const fresh = locationFreshness(at, now)
      if (!r.on_shift && fresh === 'lost') return
      const marker = new google.maps.Marker({
        map,
        position: { lat: r.location.lat, lng: r.location.lng },
        title: `${r.name} · seen ${lastSeenLabel(at, now)}`,
        label: { text: initials(r.name), color: '#ffffff', fontSize: '11px', fontWeight: '700' },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 13,
          fillColor: colourOf.get(r.id) ?? '#2563eb',
          fillOpacity: fresh === 'lost' ? 0.45 : 1,
          strokeColor: FRESHNESS_COLOUR[fresh],
          strokeWeight: 4,
        },
        zIndex: 10,
      })
      marker.addListener('click', () => {
        setSelected(r.id)
        const loc = r.location!
        const speed = loc.speed_mps != null ? ` · ${Math.round(loc.speed_mps * 3.6)} km/h` : ''
        const battery = loc.battery != null ? ` · battery ${Math.round(loc.battery * 100)}%` : ''
        infoRef.current?.setContent(
          `<div style="font:13px system-ui;min-width:180px"><b>${escapeHtml(r.name)}</b><br/>Seen ${lastSeenLabel(
            at,
            now,
          )}${speed}${battery}<br/>${r.live} assigned · ${r.on_road} on the road</div>`,
        )
        infoRef.current?.open({ map, anchor: marker })
      })
      markersRef.current.push(marker)
      bounds.extend({ lat: r.location.lat, lng: r.location.lng })
      any = true
    }
    board.riders.forEach(riderMarker)

    if (any && !fittedRef.current) {
      map.fitBounds(bounds, 48)
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() ?? 0) > 16) map.setZoom(16)
      })
      fittedRef.current = true
    }
  }, [ready, board, now, colourOf])

  // Trail of the selected rider, refreshed with the board.
  const { data: trail } = useQuery({
    queryKey: ['dispatch', 'trail', selected],
    queryFn: () => apiClient.getRiderTrail(selected!, 12),
    enabled: !!selected && ready,
    refetchInterval: 20_000,
  })
  useEffect(() => {
    const map = mapRef.current
    if (!ready || !map) return
    trailRef.current?.setMap(null)
    const pts = trail?.data?.points ?? []
    if (!selected || pts.length < 2) return
    trailRef.current = new google.maps.Polyline({
      map,
      path: pts.map((p) => ({ lat: p.lat, lng: p.lng })),
      strokeColor: colourOf.get(selected) ?? '#2563eb',
      strokeOpacity: 0.8,
      strokeWeight: 4,
    })
  }, [trail, selected, ready, colourOf])

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading map…</div>
  if (!apiKey)
    return (
      <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        Add a Google Maps key under Online → Delivery zones to see riders on a map.
      </p>
    )

  const located = board.riders.filter((r) => r.location)
  return (
    <div className="space-y-2">
      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">{error}</p>}
      <div ref={divRef} className="h-[60vh] min-h-[360px] w-full overflow-hidden rounded-lg border" />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border-2 border-green-600" aria-hidden /> live
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border-2 border-amber-600" aria-hidden /> not seen for 2+ min
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500" aria-hidden /> drop with no rider
        </span>
        {located.length === 0 && <span>No rider has shared a location yet.</span>}
        {selected && (
          <button type="button" className="underline" onClick={() => setSelected(null)}>
            Hide trail
          </button>
        )}
        <span>Drops without a map pin (typed addresses) aren’t shown.</span>
      </div>
    </div>
  )
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!)
}
